import type {
    RateDTO,
    ShippingCarrierCreateInput,
    ShippingCarrierDTO,
    ShippingCarrierUpdateInput,
    ShippingRateCreateInput,
    ShippingRateUpdateInput,
    ShippingSettingsDTO,
    ShippingSettingsInput,
    ShippingZoneCreateInput,
    ShippingZoneUpdateInput,
    ZoneDTO,
} from "@/modules/shipping/domain/contracts";
import type {
    OffsetPageDTO,
    OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type { RateDTO, ShippingCarrierDTO, ShippingSettingsDTO, ZoneDTO };

export const shippingApi = {
  carriers: {
    async list(): Promise<ShippingCarrierDTO[]> {
      return fetcher.get<ShippingCarrierDTO[]>(
        `${config.apiUrl}/shipping/carriers`,
      );
    },
    async get(id: string): Promise<ShippingCarrierDTO> {
      return fetcher.get<ShippingCarrierDTO>(
        `${config.apiUrl}/shipping/carriers/${id}`,
      );
    },
    async create(
      input: ShippingCarrierCreateInput,
    ): Promise<ShippingCarrierDTO> {
      return fetcher.post<ShippingCarrierDTO>(
        `${config.apiUrl}/shipping/carriers`,
        input,
      );
    },
    async update(
      id: string,
      input: ShippingCarrierUpdateInput,
    ): Promise<ShippingCarrierDTO> {
      return fetcher.put<ShippingCarrierDTO>(
        `${config.apiUrl}/shipping/carriers/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<ShippingCarrierDTO> {
      return fetcher.delete<ShippingCarrierDTO>(
        `${config.apiUrl}/shipping/carriers/${id}`,
      );
    },
  },
  zones: {
    async list(): Promise<ZoneDTO[]> {
      return fetcher.get<ZoneDTO[]>(`${config.apiUrl}/shipping/zones`);
    },
    async get(id: string): Promise<ZoneDTO> {
      return fetcher.get<ZoneDTO>(`${config.apiUrl}/shipping/zones/${id}`);
    },
    async create(input: ShippingZoneCreateInput): Promise<ZoneDTO> {
      return fetcher.post<ZoneDTO>(`${config.apiUrl}/shipping/zones`, input);
    },
    async update(id: string, input: ShippingZoneUpdateInput): Promise<ZoneDTO> {
      return fetcher.put<ZoneDTO>(
        `${config.apiUrl}/shipping/zones/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<{ ok: boolean }> {
      return fetcher.delete<{ ok: boolean }>(
        `${config.apiUrl}/shipping/zones/${id}`,
      );
    },
  },
  rates: {
    async list(query: OffsetPageQueryDTO): Promise<OffsetPageDTO<RateDTO>> {
      const url = new URL(`${config.apiUrl}/shipping/rates`);
      url.searchParams.set("limit", String(query.limit ?? 20));
      url.searchParams.set("offset", String(query.offset ?? 0));
      if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
      if (query.filters)
        url.searchParams.set("filters", JSON.stringify(query.filters));
      return fetcher.get<OffsetPageDTO<RateDTO>>(url.toString());
    },
    async get(id: string): Promise<RateDTO> {
      return fetcher.get<RateDTO>(`${config.apiUrl}/shipping/rates/${id}`);
    },
    async create(input: ShippingRateCreateInput): Promise<RateDTO> {
      return fetcher.post<RateDTO>(`${config.apiUrl}/shipping/rates`, input);
    },
    async update(id: string, input: ShippingRateUpdateInput): Promise<RateDTO> {
      return fetcher.put<RateDTO>(
        `${config.apiUrl}/shipping/rates/${id}`,
        input,
      );
    },
    async remove(id: string): Promise<{ ok: boolean }> {
      return fetcher.delete<{ ok: boolean }>(
        `${config.apiUrl}/shipping/rates/${id}`,
      );
    },
  },
  settings: {
    async get(): Promise<ShippingSettingsDTO> {
      return fetcher.get<ShippingSettingsDTO>(
        `${config.apiUrl}/shipping/settings`,
      );
    },
    async update(input: ShippingSettingsInput): Promise<ShippingSettingsDTO> {
      return fetcher.put<ShippingSettingsDTO>(
        `${config.apiUrl}/shipping/settings`,
        input,
      );
    },
  },
};
